import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import styled, { keyframes } from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";

import { doc, getDoc, setDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

import PhotoCropperInput from "../../../components/PhotoCropperInput";
import { D1, GradientSpan, P3 } from "../../../components/theme/text/Headings";
import {
  Blue,
  White,
  gradientColors,
  Purple,
  DarkGrey,
  White05,
  Pink,
} from "../../../components/theme/colors";
import { ButtonGradient } from "../../../components/theme/Button";

import { useAuth } from "../../../services/AuthContext";
import { db } from "../../../services/firebase.config";
import mixpanelService from "../../../services/MixpanelService";
import postHogService from "../../../services/PostHogService";

import "bootstrap/dist/css/bootstrap.min.css";

import {
  ProgressBarContainer,
  ProgressBarProgress,
} from "../../../components/theme/QuizBox";
import LayoutOnboarding from "../../../components/layout/layoutOnboarding";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const TopContent = styled.div`
  margin-top: 6vh;
  padding: 0 15px;
  animation: ${fadeInUp} 0.7s ease-out;
`;

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-height: 48px;
  padding: 0px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const BottomLogin = styled.div`
  position: absolute;
  bottom: 10vh;
  left: 2vh;
  right: 2vh;
`;

const OnboardingThree = () => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();

  const [displayName, setDisplayName] = useState("");

  const [photoURL, setPhotoURL] = useState(null); // final uploaded URL (or existing from Firestore)
  const [photoFile, setPhotoFile] = useState(null); // file to upload
  const [photoPreview, setPhotoPreview] = useState(null); // local preview URL
  const [uploading, setUploading] = useState(false);

  // Track page view on mount
  useEffect(() => {
    const props = {
      user_id: currentUserState?.uid || "anonymous",
      step: "photo",
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Onboarding Photo Viewed", props);
    postHogService.track("Onboarding Photo Viewed", props);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!currentUserState) return;

      try {
        const photoDocRef = doc(db, "public-profiles", currentUserState.uid);
        const photoDoc = await getDoc(photoDocRef);

        if (photoDoc.exists()) {
          const data = photoDoc.data();
          setDisplayName(data.displayName || "");
          if (data.photoURL) {
            setPhotoURL(data.photoURL);
          }
        }
      } catch (error) {
        // Silently fail - profile data is optional during onboarding
      }
    };

    fetchProfileData();
  }, [currentUserState]);

  const handleBack = async () => {
    navigate("/onboarding-two");
  };

  const handleUpdateProfile = async () => {
    if (!currentUserState) return;

    const photoDocRef = doc(db, "public-profiles", currentUserState.uid);
    const storage = getStorage();

    try {
      setUploading(true);
      let photoProURL = null;

      if (photoFile) {
        const picRef = ref(
          storage,
          `profile-pictures/${currentUserState.uid}/${photoFile.name}`
        );
        await uploadBytes(picRef, photoFile);
        photoProURL = await getDownloadURL(picRef);
      }

      await setDoc(
        photoDocRef,
        {
          uid: currentUserState.uid,
          displayName: displayName || "",
          photoURL: photoProURL || null,
          onboardingCompleted: true,
        },
        { merge: true }
      );

      // Track onboarding completion
      const onboardingCompleteProps = {
        user_id: currentUserState.uid,
        display_name: displayName || "",
        has_photo: !!photoProURL,
        timestamp: new Date().toISOString(),
      };
      mixpanelService.track("Onboarding Completed", onboardingCompleteProps);
      postHogService.track("Onboarding Completed", onboardingCompleteProps);

      setUploading(false);
      navigate("/onboarding-notifications");
    } catch (error) {
      console.error("Error saving profile: ", error);
      alert("There was a problem saving your profile. Please try again.");
      setUploading(false);
    }
  };

  return (
    <LayoutOnboarding>
      <ProgressBarContainer style={{ marginBottom: 8 }}>
        <ProgressBarProgress
          $background={Blue}
          $transitionBackground={Purple}
          width={`85%`}
        />
      </ProgressBarContainer>
      <HeaderRow>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "40px",
            height: "40px",
          }}
          onClick={handleBack}
        >
          <ArrowCircleLeftIcon
            style={{
              color: White05,
              fontSize: "28px",
            }}
          />
        </div>
      </HeaderRow>
      <Container>
        <Row>
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ span: 12 }}
            className="text-align-center"
          >
            <TopContent>
              <D1 color={White} className="mb-30">
                Choose a{" "}
                <GradientSpan $background={Pink} $transitionBackground={Blue}>
                  profile picture.
                </GradientSpan>
              </D1>

              {photoPreview || photoURL ? (
                <div style={{ textAlign: "center" }}>
                  <img
                    src={photoPreview || photoURL}
                    alt="Profile Preview"
                    style={{
                      width: "110px",
                      height: "110px",
                      objectFit: "cover",
                      borderRadius: "50%",
                      border: "2px solid white",
                    }}
                  />
                </div>
              ) : (
                <div className="justify-content-center">
                  <div
                    style={{
                      width: "110px",
                      height: "110px",
                      borderRadius: "50%",
                      border: "2px solid white",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: DarkGrey,
                    }}
                  >
                    <D1
                      className="mb-0 mt-0"
                      color={White}
                      style={{ fontSize: "50px" }}
                    >
                      {displayName ? displayName[0] : "?"}
                    </D1>
                  </div>
                </div>
              )}

              <PhotoCropperInput
                inputId="profile-pic-upload"
                onPhotoReady={(file, previewUrl) => {
                  setPhotoFile(file);
                  setPhotoPreview(previewUrl);
                }}
              />
              <P3
                color={White05}
                className="mt-30"
                style={{ textAlign: "center" }}
              >
                By continuing, you agree to our{" "}
                <a
                  href="https://www.vibecheckme.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: White05,
                    textDecoration: "underline",
                  }}
                >
                  Terms of Service
                </a>{" "}
                <br />
                and{" "}
                <a
                  href="https://www.vibecheckme.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: White05,
                    textDecoration: "underline",
                  }}
                >
                  Privacy Policy
                </a>
                .
              </P3>
            </TopContent>

            <BottomLogin>
              <div style={{ padding: "0 15px" }}>
                <ButtonGradient
                  width="100%"
                  $background={gradientColors["personality-check"].background}
                  $transitionBackground={
                    gradientColors["personality-check"].transitionBackground
                  }
                  onClick={handleUpdateProfile}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Complete Profile"}
                </ButtonGradient>
              </div>
            </BottomLogin>
          </Col>
        </Row>
      </Container>
    </LayoutOnboarding>
  );
};

export default OnboardingThree;
